import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Button } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import { HiFingerPrint } from "react-icons/hi";

import { TextInput, Logo, A } from "./../components";
import { ROUTES } from "../routes";
import { useToast, useAuth, usePasskeys } from "../hooks";
import axios from "axios";
import { baseURL, NOTIFICATION_TYPES } from "../utils";
import { trackEvent, identifyUser } from "../utils/posthog";

const handleLoginSuccess = (userData, setAuthToken, navigate, loginMethod) => {
  setAuthToken(userData);

  identifyUser(userData.id, { email: userData.email }, userData.team_id);
  trackEvent("session_started", {
    team_id: userData.team_id,
    login_method: loginMethod,
  });

  navigate(ROUTES.dashboard, { replace: true });
};

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const { setAuthToken } = useAuth();
  const {
    loginWithPasskey,
    isSupported,
    loading: passkeyLoading,
  } = usePasskeys();

  const getErrorMessage = (error) => {
    // Network error (server unavailable)
    if (!error.response) {
      const networkErrors = {
        ECONNREFUSED:
          "Unable to connect to server. Please check if the server is running and try again.",
        ERR_CONNECTION_REFUSED:
          "Unable to connect to server. Please check if the server is running and try again.",
        ETIMEDOUT:
          "Connection timeout. Please check your internet connection and try again.",
      };

      if (networkErrors[error.code]) {
        return networkErrors[error.code];
      }
      if (error.message === "timeout of 10000ms exceeded") {
        return "Connection timeout. Please check your internet connection and try again.";
      }
      if (error.message === "Network Error") {
        return "Network error. Please check your internet connection and try again.";
      }
      return "Unable to connect to server. Please try again later.";
    }

    // Extract error message from response data
    const { data } = error.response;
    const responseMessage =
      data?.detail ||
      data?.error ||
      data?.message ||
      (typeof data === "string" ? data : null);

    if (responseMessage) {
      return responseMessage;
    }

    // Fallback based on status code
    const statusMessages = {
      400: "Invalid email or password. Please check your credentials and try again.",
      401: "Invalid email or password. Please check your credentials and try again.",
      403: "Access denied. Please contact your administrator.",
      404: "Service not found. Please try again later.",
      429: "Too many login attempts. Please wait a moment and try again.",
      500: "Server error. Please try again later.",
      502: "Server temporarily unavailable. Please try again later.",
      503: "Server temporarily unavailable. Please try again later.",
      504: "Server temporarily unavailable. Please try again later.",
    };

    return (
      statusMessages[error.response.status] ||
      "An unexpected error occurred. Please try again."
    );
  };

  const handleSubmit = (e) => {
    if (isProcessing) {
      return;
    }

    e.preventDefault();
    setIsProcessing(true);

    axios
      .post(
        `${baseURL}/token/`,
        {
          username: e.target.email.value,
          password: e.target.password.value,
        },
        { withCredentials: true }, // Enable cookies to be set by backend
      )
      .then((response) => {
        handleLoginSuccess(response.data.user, setAuthToken, navigate, "email");
      })
      .catch((error) => {
        console.error("Login error:", error);

        const errorMessage = getErrorMessage(error);

        // Track login failure
        trackEvent("login_failed", {
          error_type: error.response?.status
            ? `http_${error.response.status}`
            : "network_error",
          error_message: errorMessage,
        });

        showToast({
          content: errorMessage,
          type: NOTIFICATION_TYPES.error,
        });
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  const handlePasskeyLogin = async () => {
    const result = await loginWithPasskey();

    if (result.success && result.data?.user) {
      handleLoginSuccess(result.data.user, setAuthToken, navigate, "passkey");
      return;
    }

    showToast({
      content: result.error,
      type: NOTIFICATION_TYPES.error,
    });

    trackEvent("login_failed", {
      error_type: "passkey_error",
      error_message: result.error,
    });
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-24 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <Logo variant={Logo.variants.default} className="mx-auto h-16 w-auto" />
        <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
          {t("pages.login.title")}
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <TextInput
            name="email"
            type="email"
            label={t("pages.login.email.label")}
            placeholder={t("pages.login.email.placeholder")}
            required
          />
          <TextInput
            name="password"
            type="password"
            label={t("pages.login.password.label")}
            placeholder={t("pages.login.password.placeholder")}
            required
          />
          <Button type="submit" color="blue" isProcessing={isProcessing}>
            {t("pages.login.submit")}
          </Button>
        </form>

        {isSupported() && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500">or</span>
              </div>
            </div>

            <Button
              color="gray"
              className="w-full"
              onClick={handlePasskeyLogin}
              isProcessing={passkeyLoading}
            >
              <HiFingerPrint className="mr-2 h-5 w-5" />
              Sign in with Passkey
            </Button>
          </>
        )}

        <div className="mt-4" />
        <A>{t("pages.login.forgotPassword")}</A>
      </div>
    </div>
  );
};
